import { Listing } from './listing.type';

const generateSrcSet = (
    img: { src?: string; width?: number }[] | undefined,
    modifier = ''
) => {
    if (!img) return '';
    let res = '';

    img.forEach((item, idx) => {
        // add srcset per width
        res += `${item.src + modifier} ${item.width}w`;

        // add comma at the end if not the last item
        if (idx < img.length - 1) {
            res += ', ';
        }
    });

    return res;
};

const responsiveListingImgSizes =
    '(min-width: 1141px) 440px, (min-width: 769px) 50vw, 100vw';

/**
 * Given an img sha from compressListings expand into a full image definition
 */
export const getListingImages = (
    listing: Listing,
    limit?: number | null
): {
    src: string;
    caption: string;
    responsiveImgList?: { width: number; src: string }[];
    sources?: { type: 'image/jpeg' | 'image/webp'; srcSet: string }[];
    srcSet?: string;
    sizes?: string;
}[] => {
    const images: {
        src: string;
        caption: string;
        responsiveImgList?: { width: number; src: string }[];
        sources?: { type: 'image/jpeg' | 'image/webp'; srcSet: string }[];
        srcSet?: string;
        sizes?: string;
    }[] = [];

    if (listing?.image?.src) {
        /**
         * Main listing img can be incorrectly formatted as below, fix the widths to that of the width specified in img url
            {
              "width": 480,
              "src": "https://lid.zoocdn.com/354/255/1321022421cf8403d4ff91b84c29b7b4917735fa.jpg"
            },
            {
              "width": 960,
              "src": "https://lid.zoocdn.com/645/430/1321022421cf8403d4ff91b84c29b7b4917735fa.jpg"
            }
         */
        const responsiveImgList = listing.image.responsiveImgList?.map((v) => ({
            width:
                v?.width === 480
                    ? 354
                    : v?.width === 960
                    ? 645
                    : v?.width ?? 645,
            src: v?.src ?? ''
        }));
        const srcSet = generateSrcSet(responsiveImgList);
        images.push({
            src: listing.image?.src,
            caption: listing.image?.caption || '',
            responsiveImgList,
            srcSet,
            sizes: responsiveListingImgSizes,
            sources: [
                {
                    type: 'image/webp',
                    srcSet: generateSrcSet(responsiveImgList, ':p')
                },
                {
                    type: 'image/jpeg',
                    srcSet
                }
            ]
        });
    }

    if (listing?.gallery && listing?.gallery.length > 0) {
        const end = limit ?? listing.gallery.length;
        for (let index = 0; index < end; index++) {
            const [caption, uri] = listing.gallery[index];
            const src = uri.startsWith('/')
                ? 'https://lid.zoocdn.com/645/430' + uri
                : uri;

            const responsiveImgList = uri.startsWith('/')
                ? [
                      {
                          width: 645,
                          src
                      },
                      {
                          width: 354,
                          src: 'https://lid.zoocdn.com/354/255' + uri
                      }
                  ]
                : [];
            const srcSet = generateSrcSet(responsiveImgList);
            images.push({
                src,
                caption: caption ?? '',
                responsiveImgList,
                srcSet,
                sizes: responsiveListingImgSizes,
                sources: [
                    {
                        type: 'image/webp',
                        srcSet: generateSrcSet(responsiveImgList, ':p')
                    },
                    {
                        type: 'image/jpeg',
                        srcSet
                    }
                ]
            });
        }
        return images;
    }

    /** Backward compatible implementation */
    listing?.otherPropertyImages?.forEach((image) => {
        // Exclude SubImage objects with no large source
        if (!image?.large) return;
        images.push({
            src: image.large,
            caption: image.caption || ''
        });
    });

    return images;
};
